'use client';
import { Suspense } from 'react';
import Script from 'next/script';
import { create } from 'zustand';
import ImpactTracking from './lib/impact-tracking/impact-tracking';
import KlaviyoIdentify from './components/klaviyo-identify/klaviyo-identify';
import getTrackingConsentCookie from './lib/osano/get-tracking-consent-cookie';

interface ScriptState {
    nostoInitialised: boolean;
    toggleNostoInitialised: () => void;
    setNostoInitialised: (value: boolean) => void;
}

export const useScript = create<ScriptState>((set) => ({
    nostoInitialised: false,
    hashedEmail: null,
    toggleNostoInitialised: () =>
        set((state) => ({ nostoInitialised: !state.nostoInitialised })),
    setNostoInitialised: (value: boolean) =>
        set(() => ({ nostoInitialised: value })),
}));

const Scripts = ({ config }) => {
    const { setNostoInitialised } = useScript();

    if(typeof (window as any) !== 'undefined') {
        (window as any).Shopify = { 
            customerPrivacy: {
                userCanBeTracked: () => {
                    const trackingContentCookie = getTrackingConsentCookie()
                    if(!trackingContentCookie) {
                        return true;
                    }
                   
                }
            }
        }
    }

    return (
        <>
            <Script
                id={'osano-script'}
                strategy="afterInteractive"
                src={`https://cmp.osano.com/AzZVb9THSVMwP4LtK/7cee66db-8e74-4787-90b8-7d1ff79ce58e/osano.js`}
                onLoad={() => {
                    (window as any).Osano.cm.addEventListener(
                        'osano-cm-consent-saved',
                        (data) => {
                            (window as any).cookie_consent = data;
                        }
                    );
                }}
            />
            {/* <Script
                id="prismic-preview"
                strategy="afterInteractive"
                src="https://static.cdn.prismic.io/prismic.js?new=true&repo=maap-mmds"
            /> */}
            {config.countryCode === 'US' && (
                <Suspense>
                    <ImpactTracking />
                </Suspense>
            )}
            {!!config.gtmContainerID && (
                <Script
                    id="gtm"
                    strategy="beforeInteractive"
                    dangerouslySetInnerHTML={{
                        __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document, 'script', 'dataLayer', '${config.gtmContainerID}');`,
                    }}
                />
            )}
            {process.env.NEXT_PUBLIC_HOTJAR_ID && (
                <Script
                    id="hotjar"
                    strategy="afterInteractive"
                    dangerouslySetInnerHTML={{
                        __html: `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)}; h._hjSettings={hjid:${process.env.NEXT_PUBLIC_HOTJAR_ID},hjsv:6}; a=o.getElementsByTagName('head')[0]; r=o.createElement('script');r.async=1; r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv; a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
                    }}
                />
            )}
            {!!config?.nostoAccountID && (
                <Script
                    strategy="afterInteractive"
                    id="nosto-script"
                    src={`https://connect.nosto.com/script/shopify/nosto.js?merchant=${config?.nostoAccountID}`}
                    onReady={() => {
                        if (typeof (window as any).nostojs !== 'undefined') {
                            (window as any).nostojs((api) =>
                                api.setAutoLoad(false)
                            );
                            setNostoInitialised(true);
                        }
                    }}
                />
            )}
            {!!config.pinterestTagId && (
                <Script
                    src="https://s.pinimg.com/ct/core.js"
                    id="pintrk-scripts"
                    strategy="afterInteractive"
                    onLoad={() => {
                        if (!(window as any).pintrk) {
                            (window as any).pintrk = function () {
                                (window as any).pintrk.queue.push(
                                    Array.prototype.slice.call(arguments)
                                );
                            };
                            let n = (window as any).pintrk;
                            (n.queue = []), (n.version = '3.0');
                        }
                        (window as any).pintrk('load', config.pinterestTagId, {
                            em: config.pinterestContactEmail,
                        }); //
                        (window as any).pintrk('page');
                    }}
                />
            )}
            {!!config.klaviyoCompanyID && (
               <>
                    <Script
                        strategy="afterInteractive"
                        src={`https://static.klaviyo.com/onsite/js/${config.klaviyoCompanyID}/klaviyo.js`}
                        onLoad={() => {
                            if (
                                (window as any) !== undefined &&
                                (window as any)._learnq === undefined
                            ) {
                                (window as any)._learnq = [];
                            }
                            if(typeof (window as any) !== 'undefined') {
                                // @ts-ignore
                                !function(){if(!window.klaviyo){window._klOnsite=window._klOnsite||[];try{window.klaviyo=new Proxy({},{get:function(n,i){return"push"===i?function(){var n;(n=window._klOnsite).push.apply(n,arguments)}:function(){for(var n=arguments.length,o=new Array(n),w=0;w<n;w++)o[w]=arguments[w];var t="function"==typeof o[o.length-1]?o.pop():void 0,e=new Promise((function(n){window._klOnsite.push([i].concat(o,[function(i){t&&t(i),n(i)}]))}));return e}}})}catch(n){window.klaviyo=window.klaviyo||[],window.klaviyo.push=function(){var n;(n=window._klOnsite).push.apply(n,arguments)}}}}();
                            }
                        }}
                    />
                    <KlaviyoIdentify />
                </>
            )}
            {!!config.metaPixelID && (
                <>
                    <Script
                        id="meta-pixel"
                        strategy="afterInteractive"
                        dangerouslySetInnerHTML={{
                            __html: ` !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod? n.callMethod.apply(n,arguments):n.queue.push(arguments)}; if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0'; n.queue=[];t=b.createElement(e);t.async=!0; t.src=v;s=b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t,s)}(window, document,'script', 'https://connect.facebook.net/en_US/fbevents.js'); fbq('init', ${config.metaPixelID}); fbq('track', 'PageView');`,
                        }}
                    />
                    {/* <noscript>
                        <img
                            height="1"
                            width="1"
                            style={{ display: 'none' }}
                            src={`https://www.facebook.com/tr?id=${config.metaPixelID}&ev=PageView&noscript=1`}
                            alt=""
                        />
                    </noscript> */}
                </>
            )}
            {config.countryCode === 'UK' && (
                <Script
                    id="sa-dynamic-optimization"
                    strategy="beforeInteractive"
                    type="text/javascript"
                    data-uuid="f312b185-ab95-4b74-ad74-e564aa34b72d” src=“data:text/javascript;base64,dmFyIHNjcmlwdCA9IGRvY3VtZW50LmNyZWF0ZUVsZW1lbnQoInNjcmlwdCIpO3NjcmlwdC5zZXRBdHRyaWJ1dGUoIm5vd3Byb2NrZXQiLCAiIik7c2NyaXB0LnNldEF0dHJpYnV0ZSgibml0cm8tZXhjbHVkZSIsICIiKTtzY3JpcHQuc3JjID0gImh0dHBzOi8vZGFzaGJvYXJkLnNlYXJjaGF0bGFzLmNvbS9zY3JpcHRzL2R5bmFtaWNfb3B0aW1pemF0aW9uLmpzIjtzY3JpcHQuZGF0YXNldC51dWlkID0gImYzMTJiMTg1LWFiOTUtNGI3NC1hZDc0LWU1NjRhYTM0YjcyZCI7c2NyaXB0LmlkID0gInNhLWR5bmFtaWMtb3B0aW1pemF0aW9uLWxvYWRlciI7ZG9jdW1lbnQuaGVhZC5hcHBlbmRDaGlsZChzY3JpcHQpOw=="
                    // @ts-ignore
                    nowprocket
                    nitro-exclude
                />
            )}
            <Script
                id="tiktok-tracking"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: ` !function (w, d, t) { w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=i+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};ttq.load('CFQO3CBC77UBIS8PJ4MG');ttq.page();`,
                }}
            />
        </>
    );
};

export default Scripts;
